import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, Mail, Search, HelpCircle, BookOpen, ArrowRight, ChevronDown,
} from "lucide-react";

import "../styles/marketing.css";
import Seo from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Reveal from "../components/marketing/Reveal";

const FAQS = [
  { question: "How do I get started with Fieldz AI?", answer: "Getting started is easy! Simply sign up for a free trial, set up your business profile, add your team members, and start managing your jobs. Our onboarding wizard will guide you through each step." },
  { question: "How do I purchase a virtual phone number?", answer: "Navigate to Settings > Phones tab. Click 'Buy Number', search for available numbers by area code, select one, and complete the purchase. The number will be instantly activated." },
  { question: "Can I upgrade or downgrade my plan anytime?", answer: "Yes! You can change your plan at any time from Settings > My Plan. Upgrades take effect immediately with prorated billing. Downgrades take effect at the start of your next billing cycle." },
  { question: "How does the technician portal work?", answer: "Technicians receive an SMS invite with a setup link. Once they create their account, they can view assigned jobs, track earnings, and communicate with customers through their dedicated dashboard." },
  { question: "How do I set up call forwarding?", answer: "Go to Settings > Phones, click the settings icon on any number, and configure your call flow. You can set up simple forwarding, IVR menus, round-robin distribution, or time-based routing." },
  { question: "What payment methods do you accept?", answer: "We accept all major credit and debit cards through our secure Stripe integration. This includes Visa, Mastercard, American Express, and Discover." },
  { question: "How do automations work?", answer: "Automations let you set up automatic responses and notifications. Go to Fieldz AI > Automations to configure SMS reminders, email notifications, call flows, and more. Each automation can be customized." },
  { question: "Can I export my data?", answer: "Yes! Most reports and lists can be exported to CSV format. Look for the 'Export' or 'Download' button on pages like Financials, Team Reports, and Job listings." },
  { question: "How does call masking work?", answer: "Call masking lets technicians call customers through your business number. When a job is assigned, a unique PIN is generated. The tech dials the masked number, enters the PIN, and is connected. The customer sees your business number, keeping personal numbers private." },
  { question: "How do I manage invoices and estimates?", answer: "Go to the Invoices page from your dashboard. You can create new invoices or estimates, send them to customers via email/SMS, track payment status, and generate PDF documents. Technicians can also create invoices from the field." },
];

const CHANNELS = [
  { icon: Mail, title: "Email Support", description: "Get help via email within 24 hours", action: "Send Email", badge: "24h Response", href: "mailto:support@fieldzai.com" },
  { icon: MessageCircle, title: "Contact Form", description: "Send us a detailed message", action: "Contact Us", badge: "Anytime", href: "/contact" },
];

const SupportPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openFaq, setOpenFaq] = useState(-1);

  const filteredFaqs = FAQS.filter(
    (f) => f.question.toLowerCase().includes(searchQuery.toLowerCase()) || f.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="support-page-root">
      <Seo
        title="Support Center | Fieldz AI"
        description="Find answers about Fieldz AI: getting started, phone numbers, technician portal, invoicing, automations, and more. Email support responds within 24 hours."
        path="/support"
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5">Support Center</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
            How can we help you?
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-xl mx-auto mb-8">
            Find answers, explore resources, or get in touch with our support team.
          </p>
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              placeholder="Search for help articles, FAQs, tutorials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mk-input"
              style={{ padding: "16px 18px 16px 48px", borderRadius: 14 }}
              data-testid="support-search"
            />
          </div>
        </div>
      </section>

      {/* Support channels */}
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-5">
            {CHANNELS.map((ch, i) => (
              <Reveal key={ch.title} delay={Math.min(i + 1, 3)}>
                <div className="h-full flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)]" data-testid={`support-channel-${i}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center">
                      <ch.icon className="w-5 h-5 text-[var(--mk-blue)]" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.12em] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                      {ch.badge}
                    </span>
                  </div>
                  <h3 className="text-[16px] font-semibold text-slate-900 mb-2">{ch.title}</h3>
                  <p className="text-[14px] text-slate-500 flex-1 mb-5">{ch.description}</p>
                  {ch.href?.startsWith("/") ? (
                    <Link to={ch.href} className="mk-btn mk-btn-outline w-full group">
                      {ch.action} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ) : (
                    <a href={ch.href} className="mk-btn mk-btn-outline w-full group">
                      {ch.action} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Docs card */}
      <section className="py-10 bg-[var(--mk-bg-2)] border-y border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link to="/docs" data-testid="get-in-touch-card">
              <div className="flex items-center gap-5 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)]">
                <div className="w-12 h-12 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center shrink-0">
                  <BookOpen className="w-6 h-6 text-[var(--mk-blue)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[16px] font-semibold text-slate-900 mb-1">Read the documentation</h3>
                  <p className="text-[14px] text-slate-500">
                    Step-by-step guides for every feature — from your first job to multi-location dispatch.
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 shrink-0 text-[var(--mk-blue)]" />
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-8">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-1.5">Frequently asked questions</h2>
            <p className="text-[14.5px] text-slate-500">Quick answers to common questions</p>
          </Reveal>
          <div className="space-y-3">
            {filteredFaqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={faq.question}
                  className={`rounded-xl border transition-colors ${isOpen ? "border-blue-200 bg-blue-50/40" : "border-slate-200 bg-white"}`}
                  data-testid={`support-faq-${i}`}
                >
                  <button onClick={() => setOpenFaq(isOpen ? -1 : i)} className="w-full flex items-center justify-between p-5 text-left">
                    <span className="text-[15px] font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 shrink-0 text-[var(--mk-blue)] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className="grid transition-[grid-template-rows] duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-[14.5px] text-slate-600 leading-[1.7]">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <HelpCircle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-[14px] text-slate-600">No results found for &ldquo;{searchQuery}&rdquo;</p>
                <p className="text-[13px] mt-1 text-slate-400">
                  Try a different search term or <Link to="/contact" className="text-[var(--mk-blue)] font-semibold hover:underline">contact support</Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/70 to-white text-center p-10 sm:p-12 shadow-[0_10px_30px_-8px_rgba(0,70,190,0.15)]">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-3">Still need help?</h2>
              <p className="text-[14.5px] text-slate-600 mb-7 max-w-md mx-auto">
                Our support team is ready to assist you. Reach out and we'll get back to you as soon as possible.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact" className="mk-btn mk-btn-primary" data-testid="contact-support-btn">
                  <Mail className="w-4 h-4" /> Contact Support
                </Link>
                <Link to="/docs" className="mk-btn mk-btn-outline" data-testid="browse-docs-btn">
                  <BookOpen className="w-4 h-4" /> Browse Docs
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default SupportPage;
